// =====================================================
// ONEFORM - PARSER DE MARKDOWN PARA FORMULÁRIOS
// =====================================================

import type { FormField, FieldType, FormSettings, ThemeConfig } from '../../types/forms';

// Limite de tamanho do input (100KB)
const MAX_INPUT_SIZE = 100 * 1024;

// Tipos de campo válidos — 25 types, idêntico a FieldType em types/forms.ts (Story saas-1.10)
const VALID_FIELD_TYPES: FieldType[] = [
  // Básicos
  'text', 'email', 'phone', 'textarea', 'select', 'multiselect',
  'checkbox', 'radio', 'date', 'datetime', 'time', 'file',
  'number', 'url', 'heading', 'paragraph',
  // Premium / Special (Story 1.1 + saas-1.10)
  'welcome_screen', 'thank_you_screen', 'opinion_scale', 'nps',
  'rating', 'picture_choice', 'yes_no', 'statement', 'redirect_url',
];

// Resultado do parsing
export interface ParseResult {
  success: boolean;
  title: string;
  description: string;
  settings: FormSettings;
  fields: FormField[];
  errors: ParseError[];
  warnings: ParseWarning[];
}

export interface ParseError {
  line: number;
  message: string;
  severity: 'error';
}

export interface ParseWarning {
  line: number;
  message: string;
  severity: 'warning';
}

// Gerar ID único para campos
const generateFieldId = (): string => {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Sanitizar string contra XSS básico
const sanitizeString = (str: string): string => {
  return str
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};

// Parsear frontmatter YAML simples
const parseFrontmatter = (content: string): {
  frontmatter: Record<string, unknown>;
  body: string;
  endLine: number;
} => {
  const lines = content.split('\n');

  // Verificar se começa com ---
  if (lines[0]?.trim() !== '---') {
    return { frontmatter: {}, body: content, endLine: 0 };
  }

  // Encontrar o fechamento do frontmatter
  let endIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i]?.trim() === '---') {
      endIndex = i;
      break;
    }
  }

  if (endIndex === -1) {
    return { frontmatter: {}, body: content, endLine: 0 };
  }

  // Parsear YAML simples (suporta apenas key: value e objetos simples)
  const frontmatter: Record<string, unknown> = {};
  let currentObject: Record<string, unknown> | null = null;
  let currentKey: string | null = null;

  for (let i = 1; i < endIndex; i++) {
    const line = lines[i];
    if (!line || line.trim() === '') continue;

    // Verificar se é uma chave de objeto (indentada)
    const indentedMatch = line.match(/^  (\w+):\s*(.*)$/);
    if (indentedMatch && currentKey && currentObject) {
      const [, key, value] = indentedMatch;
      if (key && value !== undefined) {
        // Converter valores booleanos e números
        if (value === 'true') {
          currentObject[key] = true;
        } else if (value === 'false') {
          currentObject[key] = false;
        } else if (!isNaN(Number(value)) && value.trim() !== '') {
          currentObject[key] = Number(value);
        } else {
          currentObject[key] = value.trim();
        }
      }
      continue;
    }

    // Chave de primeiro nível
    const match = line.match(/^(\w+):\s*(.*)$/);
    if (match) {
      const [, key, value] = match;
      if (key) {
        if (value && value.trim() !== '') {
          // Valor simples
          frontmatter[key] = value.trim();
          currentKey = null;
          currentObject = null;
        } else {
          // Início de objeto
          currentKey = key;
          currentObject = {};
          frontmatter[key] = currentObject;
        }
      }
    }
  }

  const body = lines.slice(endIndex + 1).join('\n');
  return { frontmatter, body, endLine: endIndex + 1 };
};

// Parsear propriedades do campo (linhas com - key: value)
const parseFieldProperties = (
  lines: string[],
  startLine: number
): { properties: Record<string, string>; endLine: number } => {
  const properties: Record<string, string> = {};
  let i = startLine;

  while (i < lines.length) {
    const line = lines[i]?.trim();

    // Se encontrar outra seção ou campo, parar
    if (!line || line.startsWith('#') || line.startsWith('##')) {
      break;
    }

    // Parsear propriedade (- key: value)
    const match = line.match(/^-\s*(\w+):\s*(.*)$/);
    if (match) {
      const [, key, value] = match;
      if (key && value !== undefined) {
        properties[key.toLowerCase()] = value.trim();
      }
    } else if (!line.startsWith('-')) {
      // Linha que não é propriedade, parar
      break;
    }

    i++;
  }

  return { properties, endLine: i };
};

// Parsear tipo de campo
const parseFieldType = (typeStr: string): FieldType => {
  const normalized = typeStr.toLowerCase().trim();

  if (VALID_FIELD_TYPES.includes(normalized as FieldType)) {
    return normalized as FieldType;
  }

  // Aliases comuns
  const aliases: Record<string, FieldType> = {
    'string': 'text',
    'texto': 'text',
    'short_text': 'text',
    'long_text': 'textarea',
    'texto_longo': 'textarea',
    'multiline': 'textarea',
    'dropdown': 'select',
    'lista': 'select',
    'multiple': 'checkbox',
    'multipla_escolha': 'checkbox',
    'single': 'radio',
    'escolha_unica': 'radio',
    'data': 'date',
    'data_hora': 'datetime',
    'hora': 'time',
    'numero': 'number',
    'arquivo': 'file',
    'upload': 'file',
    'link': 'url',
    'titulo': 'heading',
    'paragrafo': 'paragraph',
    'multi_select': 'multiselect',
    // Story saas-1.10: aliases PT-BR para os 9 types premium/special
    'boas_vindas': 'welcome_screen',
    'agradecimento': 'thank_you_screen',
    'escala': 'opinion_scale',
    'avaliacao': 'rating',
    'imagens': 'picture_choice',
    'sim_nao': 'yes_no',
    'declaracao': 'statement',
    'redirecionamento': 'redirect_url',
    // 'nps' é literal aceito pelo enum — não precisa alias
  };

  return aliases[normalized] || 'text';
};

// Parsear opções (separadas por vírgula)
const parseOptions = (optionsStr: string): string[] => {
  if (!optionsStr) return [];

  return optionsStr
    .split(',')
    .map(opt => opt.trim())
    .filter(opt => opt.length > 0);
};

// ─── Helpers para config parsing (Story saas-1.10) ───

// Parsear boolean — aceita true/false, sim/nao, yes/no, 1/0. Default false quando ambíguo.
const parseBoolean = (value: string | undefined): boolean => {
  if (!value) return false;
  const v = value.toLowerCase().trim();
  return v === 'true' || v === 'sim' || v === 'yes' || v === '1';
};

// Parsear number com fallback; ambíguo (NaN) retorna fallback silenciosamente — caller pode pushar warning.
const parseNumberSafe = (value: string | undefined, fallback: number): { value: number; valid: boolean } => {
  if (value === undefined || value === '') return { value: fallback, valid: true };
  const n = Number(value);
  if (Number.isNaN(n)) return { value: fallback, valid: false };
  return { value: n, valid: true };
};

// Parsear `picture_choice.images` com sintaxe pipe `url|label, url|label` (OQ1=a, Pax)
const parsePictureChoiceImages = (value: string | undefined): { url: string; label: string }[] => {
  if (!value) return [];
  return value.split(',')
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0)
    .map(entry => {
      const [url, label] = entry.split('|').map(s => s.trim());
      return { url: url || '', label: label || url || '' };
    })
    .filter(({ url }) => url.length > 0);
};

/**
 * Parser principal de Markdown para FormField[]
 */
export function parseMarkdownToForm(markdown: string): ParseResult {
  const errors: ParseError[] = [];
  const warnings: ParseWarning[] = [];
  const fields: FormField[] = [];

  // Validar tamanho do input
  if (!markdown || markdown.trim() === '') {
    return {
      success: false,
      title: '',
      description: '',
      settings: {},
      fields: [],
      errors: [{ line: 0, message: 'O conteúdo Markdown está vazio', severity: 'error' }],
      warnings: [],
    };
  }

  if (markdown.length > MAX_INPUT_SIZE) {
    return {
      success: false,
      title: '',
      description: '',
      settings: {},
      fields: [],
      errors: [{ line: 0, message: `O conteúdo excede o limite de ${MAX_INPUT_SIZE / 1024}KB`, severity: 'error' }],
      warnings: [],
    };
  }

  // Parsear frontmatter
  const { frontmatter, body, endLine: frontmatterEndLine } = parseFrontmatter(markdown);

  // Extrair título e descrição
  const title = typeof frontmatter.title === 'string' ? frontmatter.title : '';
  const description = typeof frontmatter.description === 'string' ? frontmatter.description : '';

  // Validar título
  if (!title) {
    errors.push({
      line: 1,
      message: 'O frontmatter deve conter um título (title:)',
      severity: 'error',
    });
  }

  // Extrair settings
  const settings: FormSettings = {};
  if (frontmatter.settings && typeof frontmatter.settings === 'object') {
    const s = frontmatter.settings as Record<string, unknown>;
    if (typeof s.showProgressBar === 'boolean') settings.showProgressBar = s.showProgressBar;
    if (typeof s.allowSave === 'boolean') settings.allowSave = s.allowSave;
    if (typeof s.notifyOnSubmit === 'boolean') settings.notifyOnSubmit = s.notifyOnSubmit;
    if (typeof s.notifyEmail === 'string') settings.notifyEmail = s.notifyEmail;
    if (typeof s.submitButtonText === 'string') settings.submitButtonText = s.submitButtonText;
    if (typeof s.nextButtonText === 'string') settings.nextButtonText = s.nextButtonText;
    if (typeof s.showBranding === 'boolean') settings.showBranding = s.showBranding;
    if (s.themeConfig && typeof s.themeConfig === 'object') {
      settings.themeConfig = s.themeConfig as ThemeConfig;
    }
  }

  // Parsear corpo do markdown
  const lines = body.split('\n');
  let currentSection: string | undefined;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const lineNumber = frontmatterEndLine + i + 1;

    // Linha vazia ou separador
    if (!line || line.trim() === '' || line.trim() === '---') {
      i++;
      continue;
    }

    const trimmedLine = line.trim();

    // Seção (# Seção: Nome)
    const sectionMatch = trimmedLine.match(/^#\s+Seção:\s*(.+)$/i);
    if (sectionMatch) {
      currentSection = sectionMatch[1]?.trim();
      i++;
      continue;
    }

    // Também aceitar apenas # Nome (sem "Seção:")
    const simpleSectionMatch = trimmedLine.match(/^#\s+(?!#)(.+)$/);
    if (simpleSectionMatch && !trimmedLine.startsWith('##')) {
      const sectionName = simpleSectionMatch[1]?.trim();
      // Verificar se não é um campo (não começa com ## )
      if (sectionName && !sectionName.includes(':')) {
        currentSection = sectionName;
        i++;
        continue;
      }
    }

    // Campo (## Label ou ## Label *)
    const fieldMatch = trimmedLine.match(/^##\s+(.+)$/);
    if (fieldMatch) {
      let label = fieldMatch[1]?.trim() || '';
      const required = label.endsWith('*');
      if (required) {
        label = label.slice(0, -1).trim();
      }

      // Parsear propriedades do campo
      const { properties, endLine } = parseFieldProperties(lines, i + 1);

      // Verificar se tem type
      if (!properties.type) {
        warnings.push({
          line: lineNumber,
          message: `Campo "${label}" não tem tipo definido, usando "text"`,
          severity: 'warning',
        });
      }

      // Criar campo
      const fieldType = parseFieldType(properties.type || 'text');

      // Verificar se tipo é válido
      if (properties.type && !VALID_FIELD_TYPES.includes(fieldType) && fieldType === 'text') {
        warnings.push({
          line: lineNumber,
          message: `Tipo "${properties.type}" não reconhecido, usando "text"`,
          severity: 'warning',
        });
      }

      const field: FormField = {
        id: generateFieldId(),
        type: fieldType,
        label: sanitizeString(label),
        required,
        block: currentSection,
      };

      // Adicionar propriedades opcionais
      if (properties.placeholder) {
        field.placeholder = sanitizeString(properties.placeholder);
      }

      if (properties.help) {
        field.helpText = sanitizeString(properties.help);
      }

      // Opções para select, checkbox, radio, multiselect
      if (['select', 'checkbox', 'radio', 'multiselect'].includes(fieldType)) {
        if (properties.options) {
          field.options = parseOptions(properties.options).map(sanitizeString);
        } else {
          warnings.push({
            line: lineNumber,
            message: `Campo "${label}" do tipo "${fieldType}" não tem opções definidas`,
            severity: 'warning',
          });
          field.options = ['Opção 1', 'Opção 2'];
        }
      }

      // Validações
      if (properties.min || properties.max || properties.minlength || properties.maxlength) {
        field.validation = {};
        if (properties.min) field.validation.min = Number(properties.min);
        if (properties.max) field.validation.max = Number(properties.max);
        if (properties.minlength) field.validation.minLength = Number(properties.minlength);
        if (properties.maxlength) field.validation.maxLength = Number(properties.maxlength);
      }

      // Tipos de arquivo aceitos
      if (fieldType === 'file' && properties.accept) {
        field.validation = field.validation || {};
        field.validation.fileTypes = properties.accept.split(',').map(t => t.trim());
      }

      // Largura do campo
      if (properties.width === 'half') {
        field.width = 'half';
      }

      // ─── Config específico per type (Story saas-1.10) ───
      // Keys já lowercased via parseFieldProperties. Valores inválidos → warning + silent default (OQ4=a, Pax).
      switch (fieldType) {
        case 'welcome_screen': {
          const cfg: Record<string, unknown> = {};
          if (properties.buttontext) cfg.buttonText = sanitizeString(properties.buttontext);
          if (properties.showdescription !== undefined) cfg.showDescription = parseBoolean(properties.showdescription);
          if (Object.keys(cfg).length > 0) field.config = cfg as typeof field.config;
          break;
        }
        case 'thank_you_screen': {
          const cfg: Record<string, unknown> = {};
          if (properties.showsharebutton !== undefined) cfg.showShareButton = parseBoolean(properties.showsharebutton);
          if (properties.redirectdelay !== undefined) {
            const parsed = parseNumberSafe(properties.redirectdelay, 0);
            if (!parsed.valid) {
              warnings.push({ line: lineNumber, message: `Campo "${label}": redirectdelay "${properties.redirectdelay}" inválido, usando 0`, severity: 'warning' });
            }
            cfg.redirectDelay = parsed.value;
          }
          if (Object.keys(cfg).length > 0) field.config = cfg as typeof field.config;
          break;
        }
        case 'opinion_scale': {
          const cfg: Record<string, unknown> = {};
          if (properties.minvalue !== undefined) {
            const parsed = parseNumberSafe(properties.minvalue, 1);
            if (!parsed.valid) warnings.push({ line: lineNumber, message: `Campo "${label}": minvalue "${properties.minvalue}" inválido, usando 1`, severity: 'warning' });
            cfg.minValue = parsed.value;
          }
          if (properties.maxvalue !== undefined) {
            const parsed = parseNumberSafe(properties.maxvalue, 10);
            if (!parsed.valid) warnings.push({ line: lineNumber, message: `Campo "${label}": maxvalue "${properties.maxvalue}" inválido, usando 10`, severity: 'warning' });
            cfg.maxValue = parsed.value;
          }
          if (properties.minlabel) cfg.minLabel = sanitizeString(properties.minlabel);
          if (properties.maxlabel) cfg.maxLabel = sanitizeString(properties.maxlabel);
          if (Object.keys(cfg).length > 0) field.config = cfg as typeof field.config;
          break;
        }
        case 'nps': {
          // NPS é 0–10 standard, sem props custom
          field.config = { minValue: 0, maxValue: 10 } as typeof field.config;
          break;
        }
        case 'rating': {
          const cfg: Record<string, unknown> = {};
          if (properties.maxrating !== undefined) {
            const parsed = parseNumberSafe(properties.maxrating, 5);
            if (!parsed.valid) warnings.push({ line: lineNumber, message: `Campo "${label}": maxrating "${properties.maxrating}" inválido, usando 5`, severity: 'warning' });
            cfg.maxRating = parsed.value;
          }
          if (properties.icontype) {
            const icon = properties.icontype.toLowerCase().trim();
            // RatingConfig.iconType: 'star' | 'heart' | 'emoji'
            if (icon === 'star' || icon === 'heart' || icon === 'emoji') {
              cfg.iconType = icon;
            } else {
              warnings.push({ line: lineNumber, message: `Campo "${label}": icontype "${properties.icontype}" inválido (use star/heart/emoji), usando star`, severity: 'warning' });
              cfg.iconType = 'star';
            }
          }
          if (Object.keys(cfg).length > 0) field.config = cfg as typeof field.config;
          break;
        }
        case 'picture_choice': {
          const cfg: Record<string, unknown> = {};
          if (properties.images) {
            const parsedImgs = parsePictureChoiceImages(properties.images);
            if (parsedImgs.length === 0) {
              warnings.push({ line: lineNumber, message: `Campo "${label}": images vazio ou malformado (use "url|label, url|label")`, severity: 'warning' });
            }
            cfg.images = parsedImgs;
          } else {
            warnings.push({ line: lineNumber, message: `Campo "${label}" do tipo "picture_choice" não tem imagens definidas`, severity: 'warning' });
            cfg.images = [];
          }
          if (properties.multiselect !== undefined) cfg.multiSelect = parseBoolean(properties.multiselect);
          field.config = cfg as typeof field.config;
          break;
        }
        case 'redirect_url': {
          const cfg: Record<string, unknown> = {};
          if (properties.url) {
            cfg.url = sanitizeString(properties.url);
          } else {
            warnings.push({ line: lineNumber, message: `Campo "${label}" do tipo "redirect_url" não tem URL definida`, severity: 'warning' });
          }
          if (properties.delay !== undefined) {
            const parsed = parseNumberSafe(properties.delay, 0);
            if (!parsed.valid) warnings.push({ line: lineNumber, message: `Campo "${label}": delay "${properties.delay}" inválido, usando 0`, severity: 'warning' });
            cfg.delay = Math.max(0, Math.min(10, parsed.value));
          }
          if (properties.opennewinnewtab !== undefined) cfg.openInNewTab = parseBoolean(properties.opennewinnewtab);
          if (properties.openinnewtab !== undefined) cfg.openInNewTab = parseBoolean(properties.openinnewtab); // alias comum
          if (Object.keys(cfg).length > 0) field.config = cfg as typeof field.config;
          break;
        }
        // yes_no e statement: sem config adicional (label/helpText cobrem)
      }

      fields.push(field);
      i = endLine;
      continue;
    }

    i++;
  }

  // Verificar se há campos
  if (fields.length === 0 && errors.length === 0) {
    warnings.push({
      line: frontmatterEndLine,
      message: 'Nenhum campo foi encontrado no Markdown',
      severity: 'warning',
    });
  }

  return {
    success: errors.length === 0,
    title: sanitizeString(title),
    description: sanitizeString(description),
    settings,
    fields,
    errors,
    warnings,
  };
}

/**
 * Validar Markdown sem parsear completamente (para preview rápido)
 */
export function validateMarkdown(markdown: string): {
  isValid: boolean;
  hasTitle: boolean;
  fieldCount: number;
  errors: string[];
} {
  if (!markdown || markdown.trim() === '') {
    return { isValid: false, hasTitle: false, fieldCount: 0, errors: ['Conteúdo vazio'] };
  }

  if (markdown.length > MAX_INPUT_SIZE) {
    return { isValid: false, hasTitle: false, fieldCount: 0, errors: ['Conteúdo muito grande'] };
  }

  const result = parseMarkdownToForm(markdown);

  return {
    isValid: result.success,
    hasTitle: !!result.title,
    fieldCount: result.fields.length,
    errors: result.errors.map(e => e.message),
  };
}

/**
 * Gerar template de exemplo
 */
export function getExampleMarkdown(): string {
  return `---
title: Formulário Completo — 25 Tipos de Campo
description: Exemplo abrangente demonstrando todos os tipos suportados pelo OneForm
settings:
  showProgressBar: true
  submitButtonText: Enviar
---

# Seção: Básicos

## Nome completo *
- type: text
- placeholder: Digite seu nome completo
- help: Como você gostaria de ser chamado

## Email de contato *
- type: email
- placeholder: seu@email.com

## Telefone
- type: phone
- placeholder: (00) 00000-0000

## Descreva seu projeto
- type: textarea
- placeholder: Conte-nos sobre sua ideia...
- help: Seja o mais detalhado possível

## Orçamento aproximado
- type: number
- placeholder: 5000
- min: 0

## Site de referência
- type: url
- placeholder: https://exemplo.com

## Data de entrega desejada
- type: date

## Data e hora da reunião
- type: datetime

## Horário preferido
- type: time

---

# Seção: Escolha

## Tipo de projeto *
- type: select
- options: Logo, Identidade Visual, Website, App, Outro

## Serviços adicionais
- type: multiselect
- options: SEO, Hospedagem, Manutenção, Copywriting

## Estilos desejados
- type: checkbox
- options: Minimalista, Moderno, Clássico, Colorido, Monocromático

## Urgência *
- type: radio
- options: Urgente (até 1 semana), Normal (2-4 semanas), Flexível

---

# Seção: Telas Especiais

## Bem-vindo ao briefing
- type: welcome_screen
- buttonText: Começar
- showDescription: true
- help: Leva cerca de 5 minutos

## Obrigado pelo seu interesse!
- type: thank_you_screen
- showShareButton: true
- redirectDelay: 0

## Redirecionando...
- type: redirect_url
- url: https://exemplo.com/obrigado
- delay: 3
- openInNewTab: false

---

# Seção: Avançados

## Qual sua satisfação geral?
- type: opinion_scale
- minValue: 1
- maxValue: 10
- minLabel: Insatisfeito
- maxLabel: Muito satisfeito

## Você recomendaria este produto?
- type: nps

## Como avalia nosso atendimento?
- type: rating
- maxRating: 5
- iconType: star

## Qual estilo você prefere?
- type: picture_choice
- images: https://picsum.photos/200/200?1|Estilo A, https://picsum.photos/200/200?2|Estilo B, https://picsum.photos/200/200?3|Estilo C
- multiSelect: false

## Aceita os termos de uso? *
- type: yes_no

## Obrigado por chegar até aqui
- type: statement
- help: Sua resposta é muito importante para nós

## Anexar referências
- type: file
- accept: image/*, application/pdf
- help: Envie imagens ou PDFs de referência

## Sobre suas preferências
- type: heading

## Preencha com atenção as próximas perguntas para personalizarmos a proposta.
- type: paragraph
`;
}
